import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";

import { UsageCard } from "../components/profile/UsageCard";
import { CreditsCard } from "../components/profile/CreditsCard";
import { CustomVoicesCard } from "../components/profile/CustomVoicesCard";
import { SavePaymentMethodDialog } from "../components/profile/SavePaymentMethodDialog";
import { SnippetCard } from "../components/profile/SnippetCard";
import { Button, Card, InputField, Tabs, Text, Tooltip, View } from "../components/base";
import { Icon } from "../components/base/Icon";
import { Dialog } from "../components/base/Dialog";
import { organizationActions } from "../redux/reducers/organizationSlice";
import { MembersCard } from "../components/profile/MembersCard";
import { organizationInvitesActions } from "../redux/reducers/organizationInvitesSlice";
import { organizationMembersActions } from "../redux/reducers/organizationMemberSlice";
import { organizationTelephonyActions, OrganizationTelephony } from "../redux/reducers/organizationTelephonySlice";
import { OrganizationVariable, organizationVariablesActions } from "../redux/reducers/organizationVariablesSlice";
import { organizationCustomVoicesActions } from "../redux/reducers/organizationCustomVoicesSlice";
import { organizationSnippetActions, OrganizationSnippet } from "../redux/reducers/organizationSnippet";
import { STRIPE_PUBLISHABLE_KEY } from "../config";
import { TelephonyCard } from "../components/profile/TelephonyCard";
import { PhoneNumbersCard } from "../components/profile/PhoneNumbersCard";
import { organizationPhoneNumberActions } from "../redux/reducers/organizationPhoneNumberSlice";
import { agentActions } from "../redux/reducers/agentSlice";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../redux";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { useAuth } from "../hooks/useAuth";
import { GlobalVariablesCard } from "../components/profile/GlobalVariablesCard";
import { OrganizationGeneralCard } from "../components/profile/OrganizationGeneralCard";
import { PageLayout } from "../components/PageLayout";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const configurationTabs = [
  { id: "organization", label: "Organization", icon: "settings" },
  { id: "global-variables", label: "Global Variables", icon: "list" },
  { id: "telephony-configurations", label: "Telephony Configurations", icon: "phone" },
  { id: "phone-numbers", label: "Phone Numbers", icon: "number" },
  { id: "audio-snippets", label: "Audio Snippets", icon: "snippet" },
  { id: "custom-voices", label: "Custom Voices", icon: "voice-over" },
];

const filterEntities = <T extends { organizationId: string }>(entities: Record<string, T>, organizationId = "") => {
  return Object.values(entities).filter((entity) => entity.organizationId === organizationId || !entity.organizationId);
};

// Derive the active section from the URL pathname
const getActiveSection = (pathname: string): "members" | "configurations" | "usage" | "billing" => {
  if (pathname.startsWith("/organization/configurations")) return "configurations";
  if (pathname.startsWith("/organization/usage")) return "usage";
  if (pathname.startsWith("/organization/billing")) return "billing";
  return "members";
};

// Derive configuration sub-tab from the hash
const getActiveConfigurationTab = (hash: string): string => {
  const cleanHash = hash.replace(/^#/, "");
  const validIds = configurationTabs.map((t) => t.id);
  // Legacy hash support
  const legacyMap: Record<string, string> = {
    variables: "global-variables",
    telephony: "telephony-configurations",
    "phone-numbers": "phone-numbers",
    snippets: "audio-snippets",
    "custom-voices": "custom-voices",
  };
  if (validIds.includes(cleanHash)) return cleanHash;
  if (legacyMap[cleanHash]) return legacyMap[cleanHash];
  return "organization";
};

// Section titles for the page header
const sectionTitles: Record<string, { title: string; description: string }> = {
  members: { title: "Members", description: "Manage team members and invitations" },
  configurations: { title: "Configurations", description: "Manage global variables, telephony, audio snippets, and custom voices" },
  usage: { title: "Usage", description: "View usage statistics" },
  billing: { title: "Billing", description: "Manage credits and payment" },
};

export const OrganizationPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector((state) => state.session.user);

  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [amount, setAmount] = useState<number>(10);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [customAmountError, setCustomAmountError] = useState<string>("");
  const createPaymentRequest = useApiRequest<{ sessionId: string }>();
  const purchasePhoneNumberRequest = useApiRequest();
  const addPhoneNumberRequest = useApiRequest();
  const releasePhoneNumberRequest = useApiRequest();
  const [isPlayingVoiceId, setIsPlayingVoiceId] = useState("");
  const currentPlayingVoice = useRef<HTMLAudioElement | null>(null);
  const { organizationId: effectiveOrganizationId, organization: effectiveOrganization } = useEffectiveOrganization();

  // Organization name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleStartEditingName = () => {
    setEditedName(effectiveOrganization?.name || "");
    setIsEditingName(true);
  };

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleSaveName = () => {
    const trimmed = editedName.trim();
    if (trimmed && trimmed !== effectiveOrganization?.name && effectiveOrganizationId) {
      dispatch(organizationActions.updateById({ id: effectiveOrganizationId, name: trimmed }));
    }
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveName();
    } else if (e.key === "Escape") {
      setIsEditingName(false);
    }
  };

  // Auto-pay state
  const [showSaveCardDialog, setShowSaveCardDialog] = useState(false);
  const autoPayRequest = useApiRequest<{ data: { enabled: boolean; threshold: number; amount: number; paymentMethodId: string | null } }>();
  const updateAutoPayRequest = useApiRequest<{ data: { enabled: boolean; threshold: number; amount: number; paymentMethodId: string | null } }>();
  const paymentMethodsRequest = useApiRequest<{ data: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number }> }>();
  const deletePaymentMethodRequest = useApiRequest<{ success: boolean }>();
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [autoPayThreshold, setAutoPayThreshold] = useState(5);
  const [autoPayAmount, setAutoPayAmount] = useState(25);

  const organizationVariablesEntities = useAppSelector((state) => state.organizationVariables.entities);
  const organizationVariables = useMemo(
    () => filterEntities(organizationVariablesEntities, effectiveOrganizationId),
    [organizationVariablesEntities, effectiveOrganizationId],
  );
  const organizationMembersEntities = useAppSelector((state) => state.organizationMembers.entities);
  const organizationMembers = useMemo(
    () => filterEntities(organizationMembersEntities, effectiveOrganizationId),
    [organizationMembersEntities, effectiveOrganizationId],
  );
  const organizationInvitesEntities = useAppSelector((state) => state.organizationInvites.entities);
  const organizationInvites = useMemo(
    () => filterEntities(organizationInvitesEntities, effectiveOrganizationId),
    [organizationInvitesEntities, effectiveOrganizationId],
  );
  const organizationTelephonyEntities = useAppSelector((state) => state.organizationTelephony.entities);
  const organizationTelephony = useMemo(
    () => filterEntities(organizationTelephonyEntities, effectiveOrganizationId),
    [organizationTelephonyEntities, effectiveOrganizationId],
  );
  const organizationTelephonyIsLoading = useAppSelector((state) => state.organizationTelephony.isLoading);
  const organizationCustomVoicesEntities = useAppSelector((state) => state.organizationCustomVoices.entities);
  const organizationCustomVoices = useMemo(
    () => filterEntities(organizationCustomVoicesEntities, effectiveOrganizationId),
    [organizationCustomVoicesEntities, effectiveOrganizationId],
  );
  const organizationCustomVoicesIsLoading = useAppSelector((state) => state.organizationCustomVoices.isLoading);
  const organizationSnippetsEntities = useAppSelector((state) => state.organizationSnippets.entities);
  const organizationSnippets = useMemo(
    () => filterEntities(organizationSnippetsEntities, effectiveOrganizationId),
    [organizationSnippetsEntities, effectiveOrganizationId],
  );
  const organizationSnippetsIsLoading = useAppSelector((state) => state.organizationSnippets.isLoading);
  const organizationPhoneNumbersEntities = useAppSelector((state) => state.organizationPhoneNumbers.entities);
  const organizationPhoneNumbers = useMemo(
    () => filterEntities(organizationPhoneNumbersEntities, effectiveOrganizationId),
    [organizationPhoneNumbersEntities, effectiveOrganizationId],
  );
  const organizationPhoneNumbersIsLoading = useAppSelector((state) => state.organizationPhoneNumbers.isLoading);

  // Check if current user is an org admin/owner (used for billing access)
  const currentMember = organizationMembers.find((member) => member.userId === user?.id);
  const isOrganizationAdmin = currentMember?.role === "admin" || currentMember?.role === "owner";

  // Fetch auto-pay settings and payment methods
  const fetchAutoPayData = useCallback(() => {
    autoPayRequest.fetch("/v1/credits/auto-pay");
    paymentMethodsRequest.fetch("/v1/payments/payment-methods");
  }, []);

  useEffect(() => {
    if (effectiveOrganization?.billingEnabled && (isOrganizationAdmin || user?.isAdmin)) {
      fetchAutoPayData();
    }
  }, [effectiveOrganization?.billingEnabled, isOrganizationAdmin, user?.isAdmin]);

  useEffect(() => {
    if (autoPayRequest.data?.data) {
      const data = autoPayRequest.data.data;
      setAutoPayEnabled(data.enabled);
      setAutoPayThreshold(data.threshold / 1_000_000);
      setAutoPayAmount(data.amount / 1_000_000);
    }
  }, [autoPayRequest.data]);

  const onSaveAutoPay = async (enabled: boolean, threshold: number, amount: number) => {
    await updateAutoPayRequest.fetch("/v1/credits/auto-pay", {
      method: "PUT",
      body: {
        enabled,
        threshold: Math.round(threshold * 1_000_000),
        amount: Math.round(amount * 1_000_000),
      },
    });
    fetchAutoPayData();
  };

  const onDeletePaymentMethod = async (id: string) => {
    await deletePaymentMethodRequest.fetch(`/v1/payments/payment-methods/${id}`, {
      method: "DELETE",
    });
    fetchAutoPayData();
  };

  // Derive active section from the pathname
  const activeSection = getActiveSection(location.pathname);
  const sectionInfo = sectionTitles[activeSection];

  // Configuration sub-tab from hash
  const [activeConfigurationTab, setActiveConfigurationTab] = useState(() => getActiveConfigurationTab(window.location.hash));

  // Sync configuration sub-tab when hash changes
  useEffect(() => {
    if (activeSection === "configurations") {
      const tabFromHash = getActiveConfigurationTab(location.hash);
      // If the tab isn't available for this tier, fall back to the first available tab
      if (configurationTabs.some((t) => t.id === tabFromHash)) {
        setActiveConfigurationTab(tabFromHash);
      } else {
        setActiveConfigurationTab(configurationTabs[0]?.id || "global-variables");
      }
    }
  }, [location.hash, activeSection, configurationTabs]);

  // Update hash when configuration sub-tab changes (only if already on configurations)
  useEffect(() => {
    if (activeSection === "configurations" && window.location.hash !== `#${activeConfigurationTab}`) {
      window.location.hash = activeConfigurationTab;
    }
  }, [activeConfigurationTab, activeSection]);

  const onClickInviteUser = (email: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.create({
          organizationId: effectiveOrganizationId,
          email,
          role,
        }),
      );
    }
  };

  const onClickCancelInvite = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.deleteById({
          organizationId: effectiveOrganizationId,
          id,
        }),
      );
    }
  };

  const onClickUpdateMemberRole = (id: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.updateById({
          organizationId: effectiveOrganizationId,
          id,
          role,
        }),
      );
    }
  };

  const onClickRemoveMember = (memberId: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.deleteById({
          organizationId: effectiveOrganizationId,
          id: memberId,
        }),
      );
    }
  };

  const onClickPurchaseAmount = async () => {
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load");

      const response = await createPaymentRequest.fetch("/v1/payments/create-session", {
        method: "POST",
        body: { amount },
      });

      const result = await stripe.redirectToCheckout({
        sessionId: response.data?.sessionId || "",
      });

      if (result.error) {
        console.error(result.error);
      }
    } catch (err) {
      console.error("Payment error:", err);
    }
  };

  // Manage voice audio event listeners
  useEffect(() => {
    if (!currentPlayingVoice.current || !isPlayingVoiceId) return;

    const audio = currentPlayingVoice.current;
    const handleEnded = () => setIsPlayingVoiceId("");
    const handlePause = () => setIsPlayingVoiceId("");
    const handleError = () => {
      setIsPlayingVoiceId("");
      currentPlayingVoice.current = null;
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [isPlayingVoiceId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentPlayingVoice.current) {
        currentPlayingVoice.current.pause();
        currentPlayingVoice.current = null;
        setIsPlayingVoiceId("");
      }
    };
  }, []);

  const onChangeOrganizationVariables = (updatedVariable: Partial<OrganizationVariable>) => {
    dispatch(
      organizationVariablesActions.updateById({
        ...updatedVariable,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddOrganizationVariable = () => {
    dispatch(
      organizationVariablesActions.create({
        name: "",
        value: "",
        masked: false,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickDeleteOrganizationVariable = async (id: string) => {
    await dispatch(
      organizationVariablesActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.create({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickUpdateTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.updateById({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickDeleteTelephony = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.deleteById({
          id,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  // Phone number handlers
  const onClickPurchasePhoneNumber = async (data: { telephonyId: string; areaCode: string; countryCode: string; friendlyName: string }) => {
    if (effectiveOrganizationId) {
      const result = await purchasePhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers/purchase`, {
        method: "POST",
        body: data,
      });
      if (!result.error) {
        dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
      }
      return result;
    }
    return { error: new Error("No organization selected") };
  };

  const onClickAddPhoneNumber = async (data: { telephonyId: string; phoneNumber: string; friendlyName: string }) => {
    if (effectiveOrganizationId) {
      const result = await addPhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers`, {
        method: "POST",
        body: data,
      });
      if (!result.error) {
        dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
      }
      return result;
    }
    return { error: new Error("No organization selected") };
  };

  const onClickUpdatePhoneNumber = (data: Partial<{ id: string; friendlyName: string; organizationId: string }>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationPhoneNumberActions.updateById({
          ...data,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickDeletePhoneNumber = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationPhoneNumberActions.deleteById({
          id,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickReleasePhoneNumber = async (id: string) => {
    if (effectiveOrganizationId) {
      await releasePhoneNumberRequest.fetch(`/v1/organizations/${effectiveOrganizationId}/phoneNumbers/${id}/release`, {
        method: "POST",
      });
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  // Custom voices handlers
  const handleCreateVoice = async (params: { name: string; description: string; language: string; file: File }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.createVoiceClone({
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
        language: params.language,
        file: params.file,
      }),
    );
  };

  const handleUpdateVoice = async (id: string, params: { name: string; description: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
      }),
    );
  };

  const handleDeleteVoice = async (voiceId: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.deleteById({
        id: voiceId,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  // Audio snippet handlers
  const handleCreateSnippet = (snippet: OrganizationSnippet) => {
    // Snippet is already created via the dialog, so we just need to refresh
    if (effectiveOrganizationId) {
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  const handleUpdateSnippet = async (id: string, params: { name: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
      }),
    );
  };

  const handleDeleteSnippet = async (id: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  useEffect(() => {
    if (effectiveOrganizationId) {
      dispatch(organizationActions.getById({ id: effectiveOrganizationId }));
      dispatch(organizationVariablesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationMembersActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationInvitesActions.get({ organizationId: effectiveOrganizationId, status: "pending" }));
      dispatch(organizationTelephonyActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationCustomVoicesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationPhoneNumberActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(agentActions.get({}));
    }
  }, [effectiveOrganizationId]);

  const renderTabContent = () => {
    switch (activeSection) {
      case "members":
        return (
          <View className="flex flex-col gap-6">
            <MembersCard
              members={organizationMembers}
              invites={organizationInvites.filter((invite) => invite.status === "pending")}
              onInviteUser={onClickInviteUser}
              onCancelInvite={onClickCancelInvite}
              onUpdateMemberRole={onClickUpdateMemberRole}
              onRemoveMember={onClickRemoveMember}
              maxVisible={20}
            />
          </View>
        );
      case "configurations":
        return (
          <View className="flex flex-col gap-6">
            <View className="border-b border-gray-200 dark:border-gray-700 -mx-1">
              <Tabs
                tabs={configurationTabs}
                activeTab={activeConfigurationTab}
                onChange={setActiveConfigurationTab}
              />
            </View>

            {activeConfigurationTab === "organization" && (
              <OrganizationGeneralCard
                organization={effectiveOrganization}
                organizationId={effectiveOrganizationId}
                isEditingName={isEditingName}
                editedName={editedName}
                onStartEditingName={handleStartEditingName}
                onChangeName={setEditedName}
                onSaveName={handleSaveName}
                onNameKeyDown={handleNameKeyDown}
                nameInputRef={nameInputRef}
              />
            )}

            {activeConfigurationTab === "global-variables" && (
              <GlobalVariablesCard
                variables={organizationVariables}
                onChange={onChangeOrganizationVariables}
                onClickAdd={onClickAddOrganizationVariable}
                onClickDelete={onClickDeleteOrganizationVariable}
              />
            )}

            {activeConfigurationTab === "telephony-configurations" && (
              <TelephonyCard
                telephonyConfigs={organizationTelephony}
                onAdd={onClickAddTelephony}
                onUpdate={onClickUpdateTelephony}
                onDelete={onClickDeleteTelephony}
                isLoading={organizationTelephonyIsLoading}
              />
            )}

            {activeConfigurationTab === "phone-numbers" && (
              <PhoneNumbersCard
                phoneNumbers={organizationPhoneNumbers}
                onPurchase={onClickPurchasePhoneNumber}
                onAdd={onClickAddPhoneNumber}
                onUpdate={onClickUpdatePhoneNumber}
                onDelete={onClickDeletePhoneNumber}
                onRelease={onClickReleasePhoneNumber}
                isPurchasing={purchasePhoneNumberRequest.isLoading}
                isAdding={addPhoneNumberRequest.isLoading}
                isDeleting={organizationPhoneNumbersIsLoading}
                isReleasing={releasePhoneNumberRequest.isLoading}
                isLoading={organizationPhoneNumbersIsLoading}
                organizationId={effectiveOrganizationId}
                phoneNumberMonthlyRate={effectiveOrganization?.phoneNumberMonthlyRate}
              />
            )}

            {activeConfigurationTab === "custom-voices" && (
              <CustomVoicesCard
                customVoices={organizationCustomVoices}
                isLoading={organizationCustomVoicesIsLoading}
                onCreateVoice={handleCreateVoice}
                onUpdateVoice={handleUpdateVoice}
                onDeleteVoice={handleDeleteVoice}
              />
            )}

            {activeConfigurationTab === "audio-snippets" && (
              <SnippetCard
                snippets={organizationSnippets}
                isLoading={organizationSnippetsIsLoading}
                organizationId={effectiveOrganizationId || ""}
                onCreateSnippet={handleCreateSnippet}
                onUpdateSnippet={handleUpdateSnippet}
                onDeleteSnippet={handleDeleteSnippet}
              />
            )}
          </View>
        );
      case "usage":
        return <UsageCard />;
      case "billing":
        if (!effectiveOrganization?.billingEnabled) return null;
        if (!isOrganizationAdmin && !user?.isAdmin) return null;
        return (
          <View className="flex flex-col gap-6">
            <CreditsCard onClickAddFunds={() => setShowPurchaseDialog(true)} />

            {/* Auto-Pay Settings */}
            <View className="flex flex-col">
              <View className="flex flex-row items-baseline gap-2 mb-3">
                <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Auto-Pay</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">— Automatically add funds when your balance falls below a threshold.</Text>
              </View>
              <Card className="p-6">
                {/* Saved Payment Methods */}
                <View className="mb-4">
                  <View className="flex flex-row items-center gap-3 flex-wrap">
                    <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Method</Text>
                    {paymentMethodsRequest.data?.data && paymentMethodsRequest.data.data.length > 0 ? (
                      paymentMethodsRequest.data.data.map((pm) => (
                        <View
                          key={pm.id}
                          className="flex flex-row items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                        >
                          <Icon
                            name="credit-card"
                            size={14}
                            className="text-gray-500"
                          />
                          <Text className="text-sm">
                            {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} **** {pm.last4}
                          </Text>
                          <Text className="text-xs text-gray-400">
                            {pm.expMonth}/{pm.expYear}
                          </Text>
                          <button
                            onClick={() => onDeletePaymentMethod(pm.id)}
                            className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 ml-1"
                            disabled={deletePaymentMethodRequest.isLoading}
                          >
                            Remove
                          </button>
                        </View>
                      ))
                    ) : (
                      <Text className="text-sm text-gray-400 dark:text-gray-500">No saved payment methods.</Text>
                    )}
                    <button
                      onClick={() => setShowSaveCardDialog(true)}
                      className="flex flex-row items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-2 py-1.5 group"
                    >
                      <View className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
                        <Icon
                          name="add"
                          size={13}
                          className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
                        />
                      </View>
                      <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                        Add payment method
                      </Text>
                    </button>
                  </View>
                </View>

                {/* Auto-Pay Toggle */}
                <View className="flex flex-col gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <View className="flex flex-row items-center justify-between">
                    <View className="flex flex-row items-center gap-2">
                      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Auto-Pay</Text>
                      {!paymentMethodsRequest.data?.data?.length && (
                        <Text className="text-xs text-gray-400 dark:text-gray-500">— Add a payment method first</Text>
                      )}
                    </View>
                    <Tooltip
                      content={!paymentMethodsRequest.data?.data?.length ? "Add a payment method to enable auto-pay" : ""}
                      placement="bottom"
                    >
                      <button
                        onClick={() => {
                          const newEnabled = !autoPayEnabled;
                          setAutoPayEnabled(newEnabled);
                          onSaveAutoPay(newEnabled, autoPayThreshold, autoPayAmount);
                        }}
                        disabled={!paymentMethodsRequest.data?.data?.length}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          autoPayEnabled ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
                        } ${!paymentMethodsRequest.data?.data?.length ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoPayEnabled ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </Tooltip>
                  </View>

                  {autoPayEnabled && (
                    <View className="flex flex-col gap-3">
                      <View className="flex flex-col sm:flex-row gap-3">
                        <View className="flex-1">
                          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">When balance falls below</Text>
                          <View className="relative">
                            <Text className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</Text>
                            <InputField
                              type="number"
                              value={String(autoPayThreshold)}
                              onChange={(value: string) => setAutoPayThreshold(Number(value) || 0)}
                              className="pl-7"
                              placeholder="5"
                            />
                          </View>
                        </View>
                        <View className="flex-1">
                          <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">Automatically add</Text>
                          <View className="relative">
                            <Text className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</Text>
                            <InputField
                              type="number"
                              value={String(autoPayAmount)}
                              onChange={(value: string) => setAutoPayAmount(Number(value) || 0)}
                              className="pl-7"
                              placeholder="25"
                            />
                          </View>
                        </View>
                      </View>
                      <View className="flex justify-end">
                        <Button
                          variant="success"
                          onClick={() => onSaveAutoPay(autoPayEnabled, autoPayThreshold, autoPayAmount)}
                          disabled={updateAutoPayRequest.isLoading || autoPayAmount < 5}
                        >
                          {updateAutoPayRequest.isLoading ? "Saving..." : "Save Settings"}
                        </Button>
                      </View>
                      {autoPayAmount < 5 && autoPayAmount > 0 && <Text className="text-xs text-red-500 dark:text-red-400">Minimum auto-pay amount is $5</Text>}
                    </View>
                  )}
                </View>
              </Card>
            </View>
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout
      title={sectionInfo.title}
      description={sectionInfo.description}
    >
      <View className="mt-6">{renderTabContent()}</View>

      <SavePaymentMethodDialog
        isOpen={showSaveCardDialog}
        onClose={() => setShowSaveCardDialog(false)}
        onSaved={fetchAutoPayData}
        stripePromise={stripePromise}
      />

      <Dialog
        isOpen={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
        title="Add Funds"
      >
        <View className="p-4 space-y-4">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Amount:</Text>
          <View className="grid grid-cols-2 gap-2">
            {[5, 10, 25, 50].map((amt) => (
              <button
                key={amt}
                onClick={() => {
                  setAmount(amt);
                  setIsCustomAmount(false);
                  setCustomAmount("");
                }}
                className={`w-full px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  amount === amt && !isCustomAmount
                    ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                ${amt}
              </button>
            ))}
          </View>

          <button
            onClick={() => {
              setIsCustomAmount(true);
              setAmount(0);
            }}
            className={`w-full px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${
              isCustomAmount
                ? "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            Enter Custom Amount
          </button>

          {isCustomAmount && (
            <View className="space-y-2">
              <Text className="text-sm text-gray-600 dark:text-gray-400">Enter custom amount (USD):</Text>
              <View className="relative">
                <Text className="absolute left-3 top-1/2 -translate-y-1/2">$</Text>
                <InputField
                  type="number"
                  value={customAmount}
                  onChange={(value) => {
                    setCustomAmount(value);
                    const numValue = Number(value) || 0;
                    if (numValue < 5 && value !== "") {
                      setCustomAmountError("Minimum amount is $5");
                      setAmount(0);
                    } else {
                      setCustomAmountError("");
                      setAmount(numValue);
                    }
                  }}
                  className={`w-full pl-7 ${customAmountError ? "border-red-500 dark:border-red-400" : ""}`}
                  placeholder="0.00"
                />
              </View>
              {customAmountError && <Text className="text-red-500 dark:text-red-400 text-sm">{customAmountError}</Text>}
            </View>
          )}

          <Button
            variant="success"
            size="auto"
            onClick={onClickPurchaseAmount}
            disabled={amount < 5 || createPaymentRequest.isLoading}
            className="justify-center w-full"
          >
            {createPaymentRequest.isLoading ? (
              <Icon
                name="loader"
                size={14}
                className="animate-spin"
              />
            ) : (
              <Icon
                name="add"
                size={14}
              />
            )}
            {createPaymentRequest.isLoading ? "Processing..." : `Add $${amount === 0 ? "0.00" : amount.toFixed(2)}`}
          </Button>
        </View>
      </Dialog>
    </PageLayout>
  );
};
